import { classnames } from 'Utils/classnames/classnames.utils';
import cookies from 'js-cookie';
import { url as urlUtils } from '@clubmed/utils';
import URI from 'urijs';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouterEvents } from 'Client/router/useRouterEvents';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { CriteriaContextProvider } from 'Components/widgets/criteria/CriteriaContext';
import { CriteriaDisplayProvider } from 'Components/widgets/criteria/CriteriaDisplayContext';
import type { SearchCriteria } from 'Components/widgets/facet-search/FacetSearch.utils';
import {
  currentUrlContainsSearchQueryParam,
  currentUrlHasNoQueryParams,
  FACET_QUERY_PARAMS,
  navigateToSearchPage,
  navigateToResortPage,
} from 'Components/widgets/facet-search/FacetSearch.utils';
import type { ProductProps } from 'Components/widgets/product/Product.utils';
import 'Components/widgets/product/Product.utils';
import { useTrackQueryChange } from 'Components/widgets/facet-search/useTrackQueryChange';
import { useTrackSearchResults } from 'Components/widgets/facet-search/useTrackSearchResults';
import type { Props as FacetSearchResultsProps } from 'Components/widgets/facet-search-results/FacetSearchResults';
import 'Components/widgets/facet-search-results/FacetSearchResults';
import type { ResortGridProps } from 'Components/widgets/products-grid/ProductsGrid';
import 'Components/widgets/products-grid/ProductsGrid';
import type { Props as SearchFormProps } from 'Components/widgets/search-form/SearchForm';
import { SearchForm } from 'Components/widgets/search-form/SearchForm';
import { t } from 'Src/i18n';
import { usePageContext } from 'Context/PageContext';
import { useAppContext } from 'Context/ReactAppContext';
import * as pageIds from 'Routes/constants';
import {
  changeSearchQuery,
  resetRedirection,
  resetSearch,
  resetCriteria,
  setIsSearchEnabled,
} from 'Store/faceting/facetingSearch.action';
import {
  facetingLoadOffsetSellRequest,
  facetingLoadProductsRequest,
  facetingSearchRequest,
} from 'Store/faceting/searchRequest.action';
import {
  activeCustomFacetsSelector,
  activeFacetsSelector,
  facetingCriteriaSelector,
  facetsSelector,
  filteredProductsSelector,
  getKidsValidationState,
} from 'Store/faceting/faceting.selector';
import { setProductComparisonSelectionMode } from 'Store/faceting/productComparison.action';
import { startLoading } from 'Store/global-loader/globalLoader.action';
import { keywordsSelector } from 'Store/search/search.selector';
import {
  getDepartureDate,
  getTripDuration,
  getDepartureCity,
  getTripFlexibility,
} from 'Store/trip-criteria/tripCriteria.selector';
import { getKids, getAdultsNumber } from 'Store/trip-information/storeHelper';
import { RESORT_PRODUCT_TYPE } from 'Store/utils/autocompleteItemHelper';
import {
  DEPARTURE_DATE_MAX_FACET_ID,
  DEPARTURE_DATE_MIN_FACET_ID,
  PRODUCT_FACET_ID,
  DURATION_FACET,
  SORT_BY_PRICE_QUERY_PARAM,
} from 'Utils/faceting/constants';
import {
  customFacetsQueryParamMapping,
  facetsQueryParamMapping,
  generateActiveFacetsQueries,
} from 'Utils/faceting/facetingUtils';
import { InputSelect } from 'Components/frameworks/form-inputs/input-select/InputSelect';
import { CriteriaFacets } from 'Components/widgets/criteria/criteria-facets/CriteriaFacets';
import type { ThumbnailProduct } from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
import { useTracking } from 'Tracking/tracking-api/useTracking';
const FacetSearchResults = dynamic(() =>
  import('Components/widgets/facet-search-results/FacetSearchResults').then(
    (x) => x.FacetSearchResults,
  ),
);
export type Props = {
  boutique?: string;
  className?: string;
  hideSearchInput?: SearchFormProps['withQuery'];
  i18n: CMSContent;
  initialIsSearchEnabled?: boolean;
  isProductComparisonModeAuthorized?: FacetSearchResultsProps['isProductComparisonModeAuthorized'];
  fullScreen?: boolean;
  offer?: Offer;
  orderByPrice?: 'asc' | 'desc';
  initialProducts?: ReadonlyArray<OfferProduct | ProductProps>;
  resetSearchAtMount?: boolean;
  resortGridProps?: ResortGridProps;
  resultsHasSeeMore?: FacetSearchResultsProps['resultsHasSeeMore'];
  resultsSeeMore?: FacetSearchResultsProps['resultsSeeMore'];
  searchPageRouteName?: string;
  shouldPersistCriteriaInUrl?: boolean;
  shouldRedirectToResortPage?: boolean;
  shouldSetDefaultDepartureCityOnCompare?: FacetSearchResultsProps['shouldSetDefaultDepartureCityOnCompare'];
  sortByPriceAscending?: boolean;
  displayActivityFilter?: boolean;
  displayChildcareFilter?: boolean;
  displayDestinationFilter?: boolean;
  displaySeasonFilter?: boolean;
  displayTypeOfStayFilter?: boolean;
  displayFlightDurationFilter?: boolean;
  virtualizedGrid?: FacetSearchResultsProps['virtualizedGrid'];
  withSearchFacets?: SearchFormProps['withFacets'];
  withSearchForm?: boolean;
  withSearchResults?: boolean;
  withWorldMap?: boolean;
  isSynchronousGrid?: boolean;
};
export const FacetSearch = (props: Props) => {
  const {
    className,
    displayActivityFilter = true,
    displayChildcareFilter = true,
    displayDestinationFilter = true,
    displaySeasonFilter = true,
    displayTypeOfStayFilter = true,
    displayFlightDurationFilter = true,
    fullScreen = false,
    orderByPrice: orderByPriceProp,
    resultsHasSeeMore,
    resortGridProps,
    shouldRedirectToResortPage = false,
    withSearchResults = false,
    withWorldMap = false,
  } = props;
  const appContext = useAppContext();
  const pageContext = usePageContext();
  const router = useRouter();
  const routerEvents = useRouterEvents();
  const { currentBreakpoint, isDesktop } = useResponsive();
  const [searchDone, setSearchDone] = useState(false);
  const [facetHasSeeMore, setFacetHasSeeMore] = useState(resultsHasSeeMore);
  const track = useTracking();
  const dispatch = useDispatch();
  const departureCity = useSelector(getDepartureCity);
  const departureDate = useSelector(getDepartureDate);
  const duration = useSelector(getTripDuration);
  const activeCustomFacets = useSelector(activeCustomFacetsSelector);
  const activeFacets = useSelector(activeFacetsSelector);
  const facets: Array<SearchFacetCategory> = useSelector(facetsSelector);
  const facetingCriteria = useSelector(facetingCriteriaSelector);
  const isLoading = useSelector(({ faceting }) => faceting.isLoading);
  const noResults = useSelector(({ faceting }) => faceting.noResults);
  const isSearchEnabled = useSelector((state) => state.faceting.isSearchEnabled);
  const keywords = useSelector(keywordsSelector);
  const kids = useSelector(getKids);
  const numberOfAdults = useSelector(getAdultsNumber);
  const kidsValidationState = useSelector(getKidsValidationState);
  const products: Array<ThumbnailProduct> = useSelector(
    (state) => filteredProductsSelector(state) || [],
  );
  const productIds = products.map(({ resortId }) => resortId).join('|');
  const query = useSelector((state) => state.faceting.query || {});
  const redirection = useSelector((state) => state.faceting.redirection);
  const flexibility = useSelector(getTripFlexibility);
  const orderByPriceState = useSelector(({ faceting }) => faceting.orderByPrice);
  const trackQueryChange = useTrackQueryChange();
  const trackSearchResults = useTrackSearchResults();
  const searchCriteria: SearchCriteria = {
    departureCity,
    departureDate,
    duration,
    keywords,
    kids,
    numberOfAdults,
    kidsValidationState,
    query,
  };
  const searchOptions: FacetingSearchOptions = useMemo(
    () => ({
      boutique: props.boutique,
      offerId: props.offer?.id,
      offerName: props.offer?.seoName,
      productIds: (props.initialProducts || []).map((product) => product.id),
      query: query.label || '',
      screenSize: currentBreakpoint,
    }),
    [props.boutique, props.initialProducts, props.offer, props.i18n, query, currentBreakpoint],
  );

  const navigateToSearch = () => {
    if (isSearchEnabled) {
      if (props.shouldPersistCriteriaInUrl && !routerEvents.transitioning) {
        const activeCustomFacetsQueries = generateActiveFacetsQueries(
          activeCustomFacets,
          customFacetsQueryParamMapping,
        );
        const activeFacetsQueries = generateActiveFacetsQueries(
          activeFacets,
          facetsQueryParamMapping,
        );
        navigateToSearchPage({
          activeCustomFacetsQueries,
          activeFacetsQueries,
          criteriaKeysToPersist: FACET_QUERY_PARAMS,
          router,
          searchCriteria,
          searchPageRouteName: props.searchPageRouteName,
        });
      }

      // In Cmta, on FacetSearch, sellers need persistent data concerning attendees to be used later by the comparator
      if (appContext.isCmta) {
        cookies.set('persistentNumberOfAdults', numberOfAdults);
        cookies.set('persistentKids', kids);
      }
    }
  };

  const search = () => {
    // We check routerReady to ensure that we do not update URL
    // or results while the router is transitioning between routes.
    if (isSearchEnabled) {
      navigateToSearch();
      dispatch(
        facetingSearchRequest({
          appContext,
          options: searchOptions,
        }),
      );
      setSearchDone(true);
    }
  };

  useEffect(() => {
    dispatch(
      facetingLoadOffsetSellRequest({
        appContext,
      }),
    );
  }, []);
  useEffect(() => {
    if (
      typeof products !== 'undefined' &&
      pageContext.pageId === pageIds.SEARCH_PAGE_ID &&
      withSearchResults &&
      isLoading === false &&
      searchDone
    ) {
      trackSearchResults();
    }
  }, [productIds, isLoading]);
  useEffect(() => {
    if (
      !isLoading &&
      noResults &&
      props.i18n.remote?.noResult?.recommendation?.resortIds?.length > 0
    ) {
      dispatch(
        facetingLoadProductsRequest({
          appContext,
          productIds: props.i18n.remote.noResult.recommendation.resortIds,
        }),
      );
    }
  }, [noResults]);

  const cleanSearch = async () => dispatch(resetSearch());

  const cleanCriteria = async () => dispatch(resetCriteria());

  useEffect(() => {
    dispatch(setProductComparisonSelectionMode(false));
    const { initialIsSearchEnabled = false } = props;

    if (!currentUrlContainsSearchQueryParam(router.query)) {
      // We always want to reset the search query, unless it's in the URL.
      dispatch(
        changeSearchQuery({
          label: '',
        }),
      );
    }

    // useEffect cannot be async because it's supposed to return a cleaning callback.
    // This immediatly invoked function is a workaround to this.
    (async function () {
      if (currentUrlHasNoQueryParams(router.query) && props.resetSearchAtMount) {
        await cleanSearch();
        await cleanCriteria();
      }

      if (departureCity) {
        // If transport is set in store, we need to trigger
        // the search request to get the departureCities facet from Pertimm.
        dispatch(setIsSearchEnabled(true));
      } else {
        dispatch(setIsSearchEnabled(initialIsSearchEnabled));
      }
    })();

    return () => {
      cleanSearch();
    };
  }, []);
  useEffect(() => {
    // In case PERTIMM response indicates a redirection, go to this page...
    if (redirection) {
      dispatch(startLoading());
      router.replace(urlUtils.normalizeUrl(redirection));
      return () => {
        // clear search query to avoid infinite loop on back #14970
        dispatch(
          changeSearchQuery({
            label: '',
          }),
        );
        dispatch(resetRedirection());
      };
    }
  }, [redirection]);
  const filtersKeysToIgnore = [
    DEPARTURE_DATE_MAX_FACET_ID,
    DEPARTURE_DATE_MIN_FACET_ID,
    DURATION_FACET,
    PRODUCT_FACET_ID,
  ];
  useEffect(() => {
    search();
  }, [
    JSON.stringify(kids),
    appContext.isCmta && JSON.stringify(numberOfAdults),
    JSON.stringify(query),
    isSearchEnabled,
    departureDate,
    duration,
    JSON.stringify(
      (facetingCriteria.filters || []).filter((facet) => {
        return !filtersKeysToIgnore.includes(facet.id);
      }),
    ),
    props.offer,
    flexibility,
  ]);
  useEffect(() => {
    navigateToSearch();
  }, [JSON.stringify(activeCustomFacets)]);

  const onSubmit = (event?: Event) => {
    const { searchPageRouteName } = props;

    if (event) {
      event.preventDefault();
    }

    const searchQuery = keywords
      ? {
          label: keywords,
        }
      : query;

    if (keywords) {
      dispatch(changeSearchQuery(searchQuery));
    }

    if (!keywords && shouldRedirectToResortPage && searchQuery.type === RESORT_PRODUCT_TYPE) {
      trackQueryChange(searchQuery, {
        internalSearchEngineNbResults: 'redirection',
      });
      trackSearchResults({
        internalSearchEngineNbResults: 'redirection',
        searchResorts: [],
      });
      navigateToResortPage({
        query: searchQuery,
        router,
        searchCriteria,
      });
    } else {
      if (!withSearchResults) {
        trackQueryChange(searchQuery);
        cleanSearch();
      }

      const activeCustomFacetsQueries = generateActiveFacetsQueries(
        activeCustomFacets,
        customFacetsQueryParamMapping,
      );
      const activeFacetsQueries = generateActiveFacetsQueries(
        activeFacets,
        facetsQueryParamMapping,
      );
      navigateToSearchPage({
        activeCustomFacetsQueries,
        activeFacetsQueries,
        criteriaKeysToPersist: FACET_QUERY_PARAMS,
        router,
        searchCriteria,
        searchPageRouteName,
      });
    }
  };

  const orderByPrice = orderByPriceState || orderByPriceProp;

  const getPriceSorter = () => {
    const uri = new URI(router.asPath);
    return (
      <InputSelect
        dataTestId="flexibility-input"
        onChange={(name, value) => {
          if (value) {
            track({
              actionGA: 'priceFilter',
              labelGA: `price_sort${value === 'asc' ? 'Ascending' : 'Descending'}`,
            });
          } else {
            track({
              actionGA: 'priceFilter',
              labelGA: 'price_sortByDefault',
            });
          }

          const searchPageUrl = uri.clone().setQuery({
            [SORT_BY_PRICE_QUERY_PARAM]: value || undefined,
          });
          router.replace(searchPageUrl.toString(), undefined, {
            shallow: true,
            scroll: false,
          });
        }}
        onFocus={() => {
          track({
            actionGA: 'priceFilter',
            labelGA: 'price_sortOpen',
          });
        }}
        options={[
          {
            label: t('faceting.sortByPrice.label'),
            value: '',
          },
          {
            label: t('faceting.sortByPrice.ascendingOrder'),
            value: 'asc',
          },
          {
            label: t('faceting.sortByPrice.descendingOrder'),
            value: 'desc',
          },
        ]}
        value={orderByPrice}
      />
    );
  };

  return (
    <div data-testid="facetSearch" className={classnames('relative isolate', className)}>
      <CriteriaContextProvider
        value={{
          trackingActionName: 'smartCriterias',
          setFacetHasSeeMore,
          displayActivityFilter,
          displayChildcareFilter,
          displayDestinationFilter,
          displaySeasonFilter,
          displayTypeOfStayFilter,
          displayFlightDurationFilter,
        }}
      >
        <CriteriaDisplayProvider>
          {props.withSearchForm && (
            <SearchForm
              className="z-2"
              doTrackQueryChange={withSearchResults}
              i18n={props.i18n}
              onSubmit={onSubmit}
              withFacets={props.withSearchFacets}
              withQuery={!props.hideSearchInput}
              showNbResultsInLayer={props.withSearchResults}
              fullScreen={fullScreen}
              layerButtonLabel={
                props.withSearchResults
                  ? (props.i18n.remote && props.i18n.remote.searchAction) || 'search'
                  : props.i18n.booking.editCriteria.validateCriteria
              }
            />
          )}
          <div className="relative isolate z-1 lg:flex lg:flex-row-reverse">
            <div className="w-full">
              {props.withSearchResults && (
                <FacetSearchResults
                  resortGridProps={resortGridProps}
                  i18n={props.i18n}
                  initialProducts={props.initialProducts}
                  isProductComparisonModeAuthorized={props.isProductComparisonModeAuthorized}
                  offer={props.offer}
                  orderByPrice={orderByPrice}
                  priceSorter={getPriceSorter()}
                  resultsHasSeeMore={facetHasSeeMore}
                  resultsSeeMore={props.resultsSeeMore}
                  isSynchronousGrid={props.isSynchronousGrid}
                  searchOptions={searchOptions}
                  shouldSetDefaultDepartureCityOnCompare={
                    props.shouldSetDefaultDepartureCityOnCompare
                  }
                  virtualizedGrid={props.virtualizedGrid}
                  facets={facets}
                  withWorldMap={withWorldMap}
                />
              )}
            </div>
            {props.withSearchFacets && isDesktop && <CriteriaFacets />}
            {!isDesktop && props.withSearchFacets && (
              <div className="sticky bottom-0 z-2 border-t bg-white p-15">
                <div id="clubmed-search-comparePortal" />
                <div id="clubmed-search-compareButtonPortal" className="flex gap-20">
                  {props.withSearchFacets && !isDesktop && <CriteriaFacets />}
                </div>
              </div>
            )}
          </div>
        </CriteriaDisplayProvider>
      </CriteriaContextProvider>
    </div>
  );
};
